import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { Stack } from "@mui/material";
import { Box, LoaderIndicator } from "..";
import downloadIcon from "../../../assets/icons/download.svg";
import pdfIcon from "../../../assets/icons/pdf.svg";
import useFileDownload from "../../../hooks/useFileDownload/useFileDownload";
import { Image } from "../image";
import { useQueryClient } from "react-query";

const FileDownload = ({ title, fileName, fileUrl, fileDescription = '', required }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const {
    fileBlob,
    fileDownload,
    isFetchingFile,
    isRefetchingFile,
    isFetchedFile,
    isErrorFile,
    errorMessageFile,
  } = useFileDownload(fileUrl);
  const [userRequestedDownload, setUserRequestedDownload] = useState(false);
  const isDownloading = isFetchingFile || isRefetchingFile;

  const handleDownload = async () => {
    if (isDownloading) return;
    setUserRequestedDownload(true);
    fileDownload();
  };

  useEffect(() => {
    // If the URL changes, never auto-download based on cached query data.
    setUserRequestedDownload(false);
    // Also clear any cached blob for this file so opening the page never triggers a download.
    queryClient.removeQueries(["download-file", fileUrl], { exact: true });
  }, [fileUrl, queryClient]);

  useEffect(() => {
    if (isErrorFile) {
      console.error("Error downloading file:", errorMessageFile);
      enqueueSnackbar("Error downloading file", {
        variant: "error",
      });
      setUserRequestedDownload(false);
    }
  }, [isErrorFile, errorMessageFile, enqueueSnackbar]);

  useEffect(() => {
    // React Query can return cached data immediately. Only trigger a browser download
    // after an explicit user click.
    if (!userRequestedDownload) return;
    if (!isRefetchingFile && isFetchedFile && fileBlob) {
      const url = window.URL.createObjectURL(fileBlob.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      link.click();
      window.URL.revokeObjectURL(url);
      setUserRequestedDownload(false);
    }
  }, [userRequestedDownload, isRefetchingFile, isFetchedFile, fileBlob, fileName]);

  return (
    <div style={{ width: '100%', maxWidth: '100%' }}>
      <div
        style={{
          fontSize: "1.6rem",
          fontWeight: 500,
          paddingLeft: "2.5rem",
          marginTop: "1.5rem",
          wordBreak: "break-word",
          overflowWrap: "break-word"
        }}
      >
        {title}
      </div>
      <Box
        className="flex justify-between align-center outlined"
        style={{ 
          padding: '0 2rem', 
          height: '80px',
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}
      >
        <div
          className="flex align-center justify-center"
          style={{ gap: "1rem", flex: 1, minWidth: 0 }}
        >
          <Image src={pdfIcon} alt="PDF Icon" width="20" height="20" style={{ flexShrink: 0 }} />
          <Stack direction="column" spacing={0} sx={{ flex: 1, minWidth: 0 }}>
            <p style={{ 
              fontSize: "16px", 
              lineHeight: "24px", 
              fontWeight: "400", 
              fontFamily: "Halcom W00 Regular,League Spartan,sans-serif !important", 
              color: "#203864", 
              textOverflow: "ellipsis", 
              overflow: "hidden", 
              whiteSpace: "nowrap", 
              maxWidth: "100%",
              wordBreak: "break-word",
              overflowWrap: "break-word"
            }}>{fileName}</p>
            <p style={{ 
              fontSize: "11px", 
              lineHeight: "14px", 
              fontWeight: "400", 
              fontFamily: "Halcom W00 Regular,League Spartan,sans-serif !important", 
              color: "#A8A8A8",
              wordBreak: "break-word",
              overflowWrap: "break-word"
            }}>{fileDescription}</p>
          </Stack>
        </div>

        <div
          style={{ paddingLeft: "1rem", flexShrink: 0, opacity: isDownloading ? 0.5 : 1 }}
          aria-disabled={isDownloading}
        >
          {isDownloading ? (
            <LoaderIndicator />
          ) : (
            <Image
              src={downloadIcon}
              alt="Download"
              style={{ cursor: "pointer" }}
              onClick={handleDownload}
            />
          )}
        </div>
      </Box>
    </div>
  );
};

export default FileDownload;
